import { c as _c } from "react/compiler-runtime";
import type { Hash } from 'viem';
import { type Address, type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { LoaderGradient } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import { getBlockExplorerName, isChainId } from '@repo/web3/utils';
import { ButtonLink } from '@/components/common/ButtonLink';
import StatusIndicator from '@/components/ProcessingDialog/StatusIndicator';
import { Status } from '@/contexts/Transaction/StateMachine/helpers';
interface TransactionStatusBlockProps {
  status: Status;
  step: number | 'final';
  label?: string;
  address?: Address;
  chainId: ChainId;
  stepIndex: number;
  stepTotal: number;
  txHash?: Hash;
  explorerUrl: string;
  isSafe: boolean;
  isAwaitingSafeExecution?: boolean;
  isSafeStatusUnavailable?: boolean;
}
const stepStatusText = (status: Status, step: number | 'final', {
  txHash,
  explorerUrl,
  explorerName,
  isSafe,
  isAwaitingSafeExecution
}: Pick<TransactionStatusBlockProps, 'txHash' | 'explorerUrl' | 'isSafe' | 'isAwaitingSafeExecution'> & {
  explorerName: string;
}) => {
  const viewLabel = `View transaction on ${explorerName}`;
  switch (status) {
    case Status.signing:
      return ` - Proceed in your ${isSafe ? 'transaction queue' : 'wallet'}`;
    case Status.pending:
      if (isAwaitingSafeExecution) return ' - Waiting for your Safe to execute it';
      return <>
          {' - Transaction processing'}
          {txHash && <>
              {' '}
              <ExternalLink href={explorerUrl} aria-label={viewLabel} underlined>
                View
              </ExternalLink>
            </>}
        </>;
    case Status.error:
      if (!txHash) return undefined;
      return <>
          {' - '}
          <ExternalLink href={explorerUrl} aria-label={`View last submitted transaction on ${explorerName}`} underlined>
            View last transaction
          </ExternalLink>
        </>;
    case Status.complete:
      if (step !== 'final') return ' - Signature successfully processed';
      return undefined;
    default:
      return undefined;
  }
};
const TransactionStatusBlock = t0 => {
  const $ = _c(62);
  const {
    status,
    step,
    label,
    address,
    chainId,
    stepIndex,
    stepTotal,
    txHash,
    explorerUrl,
    isSafe,
    isAwaitingSafeExecution: t1,
    isSafeStatusUnavailable: t2
  } = t0;
  const isAwaitingSafeExecution = t1 === undefined ? false : t1;
  const isSafeStatusUnavailable = t2 === undefined ? false : t2;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== address || $[1] !== chainId || $[2] !== explorerUrl || $[3] !== isAwaitingSafeExecution || $[4] !== isSafe || $[5] !== label || $[6] !== status || $[7] !== step || $[8] !== stepIndex || $[9] !== stepTotal || $[10] !== txHash) {
    const explorerName = isChainId(chainId) ? getBlockExplorerName(chainId) : "explorer";
    t10 = "gap-xs flex w-full flex-col";
    const t13 = status === Status.error && "text-destructive-foreground";
    let t14;
    if ($[21] !== t13) {
      t14 = cn("text-md font-medium", t13);
      $[21] = t13;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] !== label || $[24] !== t14) {
      t15 = <span className={t14}>{label}</span>;
      $[23] = label;
      $[24] = t14;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    let t16;
    if ($[26] !== address || $[27] !== chainId) {
      t16 = address && chainId ? <ButtonLink href={ChainUtils.getExplorerAddressUrl(chainId, address)} buttonProps={{
        variant: "secondary",
        size: "sm",
        label: abbreviateAddress(address)
      }} /> : null;
      $[26] = address;
      $[27] = chainId;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    if ($[29] !== t15 || $[30] !== t16) {
      t11 = <div className="gap-xs flex flex-wrap items-center">{t15}{t16}</div>;
      $[29] = t15;
      $[30] = t16;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t17;
    if ($[32] !== stepTotal) {
      t17 = Array.from({
        length: stepTotal
      });
      $[32] = stepTotal;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    if ($[34] !== status || $[35] !== stepIndex || $[36] !== t17) {
      t12 = <div className="gap-2xs flex items-center justify-stretch">{t17.map((_, i) => {
          if (i < stepIndex) {
            return <StatusIndicator key={i} status={Status.complete} />;
          }
          if (i === stepIndex) {
            return <StatusIndicator key={i} status={status} />;
          }
          return <StatusIndicator key={i} />;
        })}</div>;
      $[34] = status;
      $[35] = stepIndex;
      $[36] = t17;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    t8 = "text-3xs text-secondary-foreground gap-2xs flex items-start";
    if ($[38] !== status) {
      t9 = (status === Status.signing || status === Status.pending) && <LoaderGradient className="mt-0.5 size-3 shrink-0" aria-hidden={true} />;
      $[38] = status;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    t3 = "Step ";
    t4 = stepIndex + 1;
    t5 = "/";
    t6 = stepTotal;
    t7 = stepStatusText(status, step, {
      txHash,
      explorerUrl,
      explorerName,
      isSafe,
      isAwaitingSafeExecution
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = explorerUrl;
    $[3] = isAwaitingSafeExecution;
    $[4] = isSafe;
    $[5] = label;
    $[6] = status;
    $[7] = step;
    $[8] = stepIndex;
    $[9] = stepTotal;
    $[10] = txHash;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t13;
  if ($[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7) {
    t13 = <span>{t3}{t4}{t5}{t6}{t7}</span>;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== t13 || $[47] !== t8 || $[48] !== t9) {
    t14 = <span className={t8}>{t9}{t13}</span>;
    $[46] = t13;
    $[47] = t8;
    $[48] = t9;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== isAwaitingSafeExecution || $[51] !== isSafe || $[52] !== isSafeStatusUnavailable || $[53] !== status || $[54] !== step) {
    t15 = isAwaitingSafeExecution && status === Status.pending ? <span className="text-3xs text-secondary-foreground">{isSafeStatusUnavailable ? "Can't reach the Safe Transaction Service, still retrying. You can check the transaction's status in your Safe queue." : step === "final" ? "Transaction proposed to your Safe. This updates on its own once it is executed from your Safe queue." : "Approval proposed to your Safe. Once it is executed from your Safe queue, the next step starts here automatically."}</span> : isSafe && (status === Status.signing || status === Status.pending) && <span className="text-3xs text-secondary-foreground">Multi-sig signing may take longer — if this dialog times out, you can still execute the transaction from your Safe queue.</span>;
    $[50] = isAwaitingSafeExecution;
    $[51] = isSafe;
    $[52] = isSafeStatusUnavailable;
    $[53] = status;
    $[54] = step;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== t10 || $[57] !== t11 || $[58] !== t12 || $[59] !== t14 || $[60] !== t15) {
    t16 = <div className={t10}>{t11}{t12}{t14}{t15}</div>;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t14;
    $[60] = t15;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
};
export default TransactionStatusBlock;